<template>
  <!--
    Reusable, professional filter dropdown built on vue-select.

    What it adds on top of a bare <v-select>:
      • Persistent search (multi-select only) — the typed text is kept after
        picking an option, so you can type once and tick several matches
        without re-typing. Single-select clears as usual.
      • Sticky "Select all / Clear all" header for multi-select, computed from
        the options via the same `reduce` the parent passes (null/“NOT
        ASSIGNED” sentinels are excluded, matching the old behaviour).
      • A footer showing how many items are selected.
      • Match highlighting in the default option row.
      • Polished styling (rounded control, pill chips, custom scrollbar,
        selected check-mark) scoped under .filter-select.

    The parent keeps full control of the data: it passes `options`, `label`,
    `reduce`, etc. Custom option rendering (e.g. user avatars) is forwarded
    through the #option / #selected-option scoped slots.
  -->
  <v-select
      ref="select"
      class="filter-select"
      :class="{ 'filter-select--multiple': isMultiple }"
      v-bind="selectProps"
      v-model="model"
      :options="options"
      :label="label"
      :reduce="reduce"
      :multiple="isMultiple"
      :clearable="clearable"
      :close-on-select="computedCloseOnSelect"
      :clear-search-on-select="!isMultiple"
      :deselect-from-dropdown="true"
      :placeholder="placeholder"
      @search="onSearch"
  >
    <!-- Multi-select bulk actions, pinned to the top of the menu. -->
    <template v-if="isMultiple" #list-header>
      <div class="filter-select__actions">
        <button type="button" class="filter-select__action filter-select__action--all"
                @mousedown.stop.prevent="selectAll">
          <feather-icon icon="CheckSquareIcon" size="13" class="mr-25"/>
          {{ $t('filters.selectAll') }}
        </button>
        <button type="button" class="filter-select__action filter-select__action--clear"
                @mousedown.stop.prevent="unselectAll">
          <feather-icon icon="SquareIcon" size="13" class="mr-25"/>
          {{ $t('filters.unselectAll') }}
        </button>
      </div>
    </template>

    <!-- Default option row: forward the parent slot if given, else render the
         label with the current search term highlighted. -->
    <template #option="option">
      <slot name="option" v-bind="option">
        <span class="filter-select__option-label" v-html="highlight(optionLabel(option))"/>
      </slot>
    </template>

    <!-- Forward custom selected-chip rendering when the parent provides it. -->
    <template v-if="$scopedSlots['selected-option']" #selected-option="option">
      <slot name="selected-option" v-bind="option"/>
    </template>

    <!-- Selected-count footer (multi-select only, when something is chosen). -->
    <template v-if="isMultiple" #list-footer>
      <div v-if="selectedCount > 0" class="filter-select__footer">
        {{ $t('filters.selectedCount', { count: selectedCount }) }}
      </div>
    </template>

    <template #no-options>
      <div class="filter-select__empty">{{ $t('filters.noResults') }}</div>
    </template>
  </v-select>
</template>

<script>
export default {
  name: 'BaseFilterSelect',
  props: {
    value: { required: true },
    options: { type: Array, default: () => [] },
    label: { type: String, default: 'label' },
    reduce: { type: Function, default: (o) => o },
    multiple: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    // When null the close behaviour is derived: single closes on select,
    // multiple stays open so you can keep ticking matches.
    closeOnSelect: { type: Boolean, default: null },
    placeholder: { type: String, default: '' },
    selectProps: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      searchTerm: '',
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    isMultiple() {
      return this.multiple || this.selectProps.multiple === true;
    },
    computedCloseOnSelect() {
      if (this.closeOnSelect !== null) {
        return this.closeOnSelect;
      }
      return !this.isMultiple;
    },
    selectedCount() {
      if (Array.isArray(this.value)) {
        return this.value.length;
      }
      return this.value != null ? 1 : 0;
    },
    // Every selectable option value (via reduce), minus null sentinels such
    // as the "NOT ASSIGNED" rows the parents prepend.
    selectableValues() {
      return this.options
          .map((o) => this.reduce(o))
          .filter((v) => v !== null && v !== undefined);
    },
  },
  methods: {
    onSearch(term) {
      this.searchTerm = term || '';
    },
    optionLabel(option) {
      if (option == null) return '';
      if (typeof option === 'object') {
        return option[this.label] != null ? String(option[this.label]) : '';
      }
      return String(option);
    },
    // Wrap the matched part of the label so the search term stands out.
    highlight(text) {
      const safe = this.escapeHtml(text);
      const term = this.searchTerm.trim();
      if (!term) return safe;
      const re = new RegExp(`(${this.escapeRegExp(term)})`, 'ig');
      return safe.replace(re, '<mark class="filter-select__mark">$1</mark>');
    },
    escapeHtml(str) {
      return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
    },
    escapeRegExp(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    selectAll() {
      this.$nextTick(() => this.$emit('input', [...this.selectableValues]));
    },
    unselectAll() {
      this.$nextTick(() => this.$emit('input', []));
    },
  },
};
</script>

<style lang="scss" scoped>
// All vue-select internals are global-classed, so reach them with ::v-deep,
// kept under .filter-select so nothing leaks to other selects in the app.
.filter-select {
  ::v-deep .vs__dropdown-toggle {
    border: 1px solid rgba(34, 41, 47, 0.18);
    border-radius: 8px;
    padding: 2px 4px 4px;
    min-height: 34px;
    background: transparent;
    transition: border-color .15s ease, box-shadow .15s ease;
  }
  // Cap the selected-chips area so picking many options (e.g. Select all)
  // never grows the control past ~2 rows — extra chips scroll instead.
  ::v-deep .vs__selected-options {
    max-height: 60px;
    overflow-y: auto;
    flex-wrap: wrap;
    scrollbar-width: thin;
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 123, 255, 0.30);
      border-radius: 6px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
  }
  &.vs--open ::v-deep .vs__dropdown-toggle,
  ::v-deep .vs__dropdown-toggle:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }

  // Selected items as soft pill chips.
  ::v-deep .vs__selected {
    background: rgba(0, 123, 255, 0.10);
    color: #0062cc;
    border: none;
    border-radius: 6px;
    padding: 2px 6px;
    margin: 3px 2px 0;
    font-size: 0.78rem;
    font-weight: 600;
    .vs__deselect {
      fill: #0062cc;
      opacity: 0.7;
      margin-left: 4px;
      &:hover { opacity: 1; }
    }
  }

  ::v-deep .vs__search,
  ::v-deep .vs__search:focus {
    font-size: 0.85rem;
    padding-left: 6px;
  }

  ::v-deep .vs__dropdown-menu {
    border: 1px solid rgba(34, 41, 47, 0.10);
    border-radius: 10px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
    padding: 4px;
    margin-top: 4px;
    max-height: 280px;

    // Custom scrollbar — matches the calendar cells.
    scrollbar-width: thin;
    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 123, 255, 0.30);
      border-radius: 8px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    &::-webkit-scrollbar-thumb:hover { background-color: rgba(0, 123, 255, 0.55); }
  }

  ::v-deep .vs__dropdown-option {
    border-radius: 6px;
    padding: 7px 10px;
    margin: 1px 0;
    font-size: 0.85rem;
    color: #5e5873;
    display: flex;
    align-items: center;
    white-space: normal;
  }
  ::v-deep .vs__dropdown-option--highlight {
    background: rgba(0, 123, 255, 0.12);
    color: #0062cc;
  }
  // Selected rows get a trailing check-mark (deselectFromDropdown keeps them
  // visible in the list so they can be toggled off).
  ::v-deep .vs__dropdown-option--selected {
    font-weight: 600;
    color: #0062cc;
    &::after {
      content: '\2713';
      margin-left: auto;
      padding-left: 8px;
      font-weight: 700;
    }
  }

  .filter-select__option-label { width: 100%; }
  ::v-deep .filter-select__mark {
    background: rgba(255, 206, 86, 0.45);
    color: inherit;
    padding: 0 1px;
    border-radius: 2px;
  }
}

// Sticky bulk-action header.
.filter-select__actions {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  gap: 6px;
  padding: 6px;
  margin: -4px -4px 4px;
  background: #fff;
  border-bottom: 1px solid rgba(34, 41, 47, 0.08);
}
.filter-select__action {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
  &--all {
    color: #28c76f;
    background: rgba(40, 199, 111, 0.10);
    &:hover { background: rgba(40, 199, 111, 0.20); }
  }
  &--clear {
    color: #ea5455;
    background: rgba(234, 84, 85, 0.10);
    &:hover { background: rgba(234, 84, 85, 0.20); }
  }
}

.filter-select__footer {
  position: sticky;
  bottom: 0;
  margin: 4px -4px -4px;
  padding: 6px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6e6b7b;
  text-align: right;
  background: #fff;
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}
.filter-select__empty {
  padding: 10px;
  text-align: center;
  font-size: 0.82rem;
  color: #b9b9c3;
}

// ----- Dark theme -----
.dark-layout {
  .filter-select ::v-deep .vs__dropdown-toggle { background: transparent; border-color: rgba(255, 255, 255, 0.14); }
  .filter-select ::v-deep .vs__dropdown-menu { background: #283046; border-color: rgba(255, 255, 255, 0.10); }
  .filter-select ::v-deep .vs__dropdown-option { color: #d0d2d6; }
  .filter-select__actions { background: #283046; border-color: rgba(255, 255, 255, 0.08); }
  .filter-select__footer { color: #b4b7bd; background: #283046; border-color: rgba(255, 255, 255, 0.08); }
}
</style>
